import { FC } from "react";
import {
  DetectorListType,
  InstallationTypes,
  IsotoposType,
  UserType,
} from "./genericTables.types";

export interface SampleRegistrationTypes {
  id: number;
  analyst: Analyst;
  high_date: string;
  installation: Installation;
  year: number;
  month: number;
  week: number;
  reason: Reason;
  sampling_points: SamplingPoints;
  sample_type: SampleType;
  shared: boolean;
  client_reference?: string;
  masl_reference: string;
  accumulation: Accumulation;
  sample_number: number;
  analysis_type: AnalysisType[];
  conditioning?: Conditioning;
  processings?: Processing[];
  sampling?: Sampling;
  sampleManagement?: SampleManagement;
  comments: Comment[];
  calculation_date: string;
  preparations: PreparationTypes[];
}

export interface Analyst {
  id: number;
  email: string;
}

export interface Installation {
  id: number;
  codigo: string;
  descripcion: string;
}

export interface Reason {
  id: number;
  codigo: string;
  descripcion: string;
}

export interface SamplingPoints {
  id: number;
  instalacion: string;
  codigo: string;
  descripcion: string;
  distancia: string;
  sector: string;
}

export interface SampleType {
  id: number;
  codigo: string;
  descripcion: string;
  unidades: number;
}

export interface Accumulation {
  id: number;
  name: string;
  code: string;
}

export interface AnalysisType {
  id: number;
  codigo: string;
  descripcion: string;
  isotope_type: string;
  parte: any;
}

export interface Conditioning {
  id: number;
  code_procedure: any;
}

export interface Processing {
  id: number;
  sample_quantity: any;
}

export interface Sampling {
  id: number;
  year: number;
}

export interface SampleManagement {
  id: number;
  date_start_reception: string;
}

export interface Comment {
  id: number;
  analyst: Analyst2;
  comment: string;
  date_time: string;
  report?: boolean;
  internal?: boolean;
}

export interface Analyst2 {
  id: number;
  email: string;
}

export interface IFormSamplesInputs {
  client_reference?: string;
  masl_reference?: string;
  installation?: InstallationTypes;
  year?: string;
  analysis?: AnalysisType;
  sample_type?: SampleType;
  reason?: Reason;
  sampling_points?: SamplingPoints;
  shared?: { id: boolean };
  accumulation?: Accumulation;
  date_to?: string;
  date_from?: string;
  is_reception?: string;
  start_week?: number;
  end_week?: number;
}

export interface IFormNewSamples {
  id?: number;
  analyst?: string | UserType | Analyst;
  installation?: Installation | string;
  high_date?: string | Date;
  year?: string | number;
  month?: string | number;
  week?: string | number;
  reason?: string | Reason;
  sampling_points?: string | SamplingPoints;
  sample_type?: string | SampleType;
  shared?: boolean;
  client_reference?: string;
  masl_reference?: string;
  accumulation?: string | Accumulation;
  sample_number?: string | number;
  analysis_type?: number | number[];
}

export interface IFormConditioning {
  type_sampler: string;
  labelType?: string;
  typeAnalysis?: AnalysisType;
  processing_category?: string[];
  rowsCurvaDryingCalcination: RowsCurvaDryingCalcination[];
  rowsCurvaDrying: RowsCurvaDrying[];
  rowsCurvaCalcination: RowsCurvaCalcination[];
  rowsCurvaCalcinationDrying: RowsCurvaCalcinationDrying[];
  source_app_calculation_info_file: File;
  calculation_date_countig_file_exe: string;
  detector_file: string;
  calculation_equipment: Equipment[];
  first_counting_time: string;
}

export interface RowsCurvaDryingCalcination {
  id: string;
  label: string;
  capsule_dry_1: string;
  capsule_dry_2: string;
  total: string;
}

export interface RowsCurvaDrying {
  id: string;
  label: string;
  capsule_dry_1: string;
  capsule_dry_2: string;
  total: string;
}

export interface RowsCurvaCalcination {
  id: string;
  label: string;
  capsule_1: string;
  capsule_2: string;
  capsule_3: string;
  total: string;
}

export interface RowsCurvaCalcinationDrying {
  id: string;
  label: string;
  capsule_1: string;
  capsule_2: string;
  capsule_3: string;
  total: string;
}

export interface SamplingTypes {
  id: number;
  analyst: Analyst;
  date_start_sampling: string;
  date_end_sampling: string;
  year: number;
  week: number;
  amount_sample: string;
  unit_types: UnitTypes;
  s2_kg: string;
  ll2_m: string;
  m3_m2: string;
  proc_take_sample: string;
  masl_sampler: MaslSampler;
  type_sampler: string;
}

export interface Analyst {
  id: number;
  email: string;
}

export interface UnitTypes {
  id: number;
  codigo: string;
  descripcion: string;
}

export interface MaslSampler {
  id: number;
  email: string;
}

export enum InitialPhases {
  Sampling = 0,
  Management = 1,
  Conditioning = 2,
  Processing = 3,
}

export enum ConditionPhases {
  Initial = "initial",
  Analysis = "analysis",
}

export type ConditionPhasesTypes = "initial" | "analysis";

export enum AnalysisPhasesTritio {
  PREPARATION = 0,
  COUNTING = 1,
  CALCULATION = 2,
}

export enum AnalysisPhasesFe55 {
  PREPARATION = 0,
  MEASUREMENT = 1,
  COUNTING = 2,
  CALCULATION = 3,
}

export interface ResultTypes {
  id: number;
  activity: string;
  uncertainty: string;
  lid: string;
  calculation: Calculation;
  efficiency: string;
  cte_desintegration: string;
  time_increase: string;
  primary_pattern_activity: string;
  primary_pattern_uncertainty: string;
  primary_pattern_mass_mpp: string;
  primary_pattern_mass_uncertainty: string;
  secondary_pattern_mass: string;
  secondary_pattern_mass_uncertainty: string;
  calibration_source_mass: string;
  u: string;
  calibration_source_cpm: string;
  calibration_source_count_rate_uncertainty_squared: string;
  secondary_pattern_activity_calibration_source: string;
  activity_uncertainty_squared: string;
  relative_uncertainty: string;
  inverse_squared_uncertainty: string;
  count_uncertainty_squared: string;
  sample_quantity_uncertainty_squared: string;
  decay_factor_uncertainty_squared: string;
  critical_limit: string;
  to_reports: boolean;
}

export interface IsotopeResultCalculationTypes {
  isotopo: string;
  isotopoID?: string;
  actividad: string;
  incertidumbre: string;
  lid: string;
  lc: string;
}

export interface IsotopeResultCalculationPOSTTypes {
  isotope: number;
  activity: string;
  uncertainty: string;
  lid: string;
  lc: string;
  requested_by_client: number;
  calculation: number;
  id: number;
}

export interface IsotopeResultCalculationResponseTypes {
  id: number;
  isotope: IsotoposType;
  activity: string;
  uncertainty: string;
  lid: string;
  lc: string;
  requested_by_client: number;
  calculation: { id: number };
}

export interface Calculation {
  id: number;
}

export interface PreparationTypes {
  id: number;
  sample_registration: any;
  analysis_type: AnalysisType[];
  analyst: Analyst;
  preparation_procedure: PreparationProcedure;
  start_date: string;
  end_date: string;
  amount_sample: string;
  equipment: Equipment[];
  scintillator_lot_number: ScintillatorLotNumber;
  unit_types: UnitTypes;
  valid_preparation: string;
  countings?: Counting[];
  aa_measures?: MeasurementTypes[];
  calculation_procedure: PreparationProcedure;
  sampledAirVolumeSilicagel: string;
  calculation_date: string;
  total_sample_water_volume: number;
  sample_count_vapor: number;
  sampled_air_volume: number;
  number_of_traps: number;
  sampled_air_volume_silicagel: number;
  analyzed_air_volume: number;
  analyzed_water_volume: number;
  volumeRegistries?: number[];
  silicagelRegistries?: number[];
  planchette?: string;
  planchette_with_precipitate?: string;
  type?: string;
  yellow_precipitate_silver_nitrate?: number;
  violet_coloration?: number;
  carrier_concentration?: string;
  performance?: number;
  amount_ashes?: string;
  amount_equivalent?: string;
  mg_torta?: string;
  start_date_precipitation?: string;
  planchette_with_precipitate_1?: number;
  planchette_with_precipitate_2?: number;
  planchette_with_precipitate_3?: number;
  planchette_with_precipitate_4?: number;
  planchette_with_precipitate_5?: number;
  preparation_procedure_gross?: string;
  electrodeposición?: string;
}

export interface AnalysisType {
  id: number;
  codigo: string;
  descripcion: string;
  isotope_type: string;
  isotope_code: string;
  parte: any;
}

export interface Analyst {
  id: number;
  email: string;
}

export interface PreparationProcedure {
  id: number;
  codmue: string;
  codinst: string;
  proced: string;
}

export interface Equipment {
  id: number;
  codigo: string;
  descripcion: string;
  codet: string;
  codigoviejo: any;
  is_geometry: boolean;
}

export interface ScintillatorLotNumber {
  id: number;
  name: string;
  reactive_number: number;
  number_lot?: number;
  start_date_use: string;
  end_date_use: string;
  concentration: string;
}

export interface UnitTypes {
  id: number;
  codigo: string;
  descripcion: string;
}

export interface Counting {
  id: number;
  sample_registration: any;
  analysis_type: AnalysisType2[];
  analyst: Analyst2;
  counting_procedure: CountingProcedure;
  equipo: Equipment2;
  counting_date: string;
  sqp_tsie_white: string;
  sqp_tsie_sample: string;
  variation_sqp_tsie: string;
  applied_criterion: number;
  counting_valid: string;
  sample_cpm: string;
  gross_white_cpm: string;
  total_sample_counting_time: string;
  total_white_counting_time: string;
  calculations: Calculation[];
  total_cpm_sample: string;
  equipment?: Equipment;
  detectors?: DetectorListType;
  unit_types: UnitTypes;
  noise_back_1mc: number;
  amount_sample: string;
  second_counting_cpm: string;
  first_counting_cpm: string;
  first_counting_time: string;
}

export interface AnalysisType2 {
  id: number;
  codigo: string;
  descripcion: string;
  parte: any;
}

export interface Analyst2 {
  id: number;
  email: string;
}

export interface CountingProcedure {
  id: number;
  codmue: string;
  codinst: string;
  proced: string;
}

export interface Equipment2 {
  id: number;
  codigo: string;
  descripcion: string;
  codet: string;
  codigoviejo: any;
}

export interface Calculation {
  id: number;
  preparation: string;
  counting: string;
  calculation: string;
  start_date_time: string;
  end_date_time: string;
  sample_quantity: string;
  units: string;
  tsie_white: string;
  tsie_sample: string;
  shared: boolean;
  laboratory: Laboratory;
  equipment: string;
  counting_date: string;
  sample_gross_cpm: string;
  gross_target_cpm: string;
  total_time_counting: string;
  total_white_counting_time: string;
  counting_entity: CountingEntity;
  analyst: Analyst;
  calculation_date?: string;
  calculation_procedure?: PreparationProcedure;
  result?: CountingEntity;
  calculation_method?: string;
  performance?: number;
  mg_torta?: string;
  calculation_fondo?: string;
  exposition_days?: number;
  transit_days?: number;
  error_lecturas_usv?: number;
  valor_media_lecturas_usv?: number;
  lectura_4_usv?: number;
  lectura_3_usv?: number;
  lectura_2_usv?: number;
  lectura_1_usv?: number;
  dosimetro?: number;
  group?: number;
  is_read?: string;
  is_delete?: string;
  fondos?: Fondo;
  fondo_alfa?: Fondo;
  source_app_calculation_info_file?: string;
}

export interface Laboratory {
  id: number;
  codigo: string;
  descripcion: string;
}

export interface Fondo {
  id: number | string;
  fondo: any;
}

export interface CountingEntity {
  id: number;
  to_reports: boolean;
}

export enum LabelTypesOptions {
  NUMERICO = "numerico",
  CIENTIFICO = "cientifico",
  CIENTIFICOCONCERO = "cientificoconcero",
}
export interface LabelInfo {
  label: string;
  decimals: number;
  type: LabelTypesOptions;
  order: number;
  id: string;
}

export type LabelKeys =
  | "efficiency"
  | "cte_desintegration"
  | "time_increase"
  | "primary_pattern_activity"
  | "primary_pattern_uncertainty"
  | "primary_pattern_mass_mpp"
  | "primary_pattern_mass_uncertainty"
  | "secondary_pattern_mass"
  | "secondary_pattern_mass_uncertainty"
  | "relative_uncertainty"
  | "inverse_squared_uncertainty"
  | "count_uncertainty_squared"
  | "sample_quantity_uncertainty_squared"
  | "decay_factor_uncertainty_squared"
  | "critical_limit"
  | "inc_mass_with_greatest_amount_of_scintillator"
  | "cpm_of_the_fc_with_largest_amount_of_scintillator"
  | "inc_count_squared_fc_with_greater_amount_of_scintillator"
  | "secondary_pattern_activity_of_the_fc_with_the_greatest_amount"
  | "inc_squared_of_the_activity_of_the_secondary_standard"
  | "mass_with_greatest_amount_of_scintillator"
  | "tertiary_patron_mass"
  | "inc_mass_uncertainty_tertiary_pattern"
  | "tsie_sqp_patron_without_agent_extintor"
  | "inc_tsie_sqp_patron_without_agent_extintor"
  | "inc_count_squared_fc_without_agent_extintor"
  | "activity_source_calibration_without_agent_extintor"
  | "inc_squared_activity_source_calibration_without_agent_extintor"
  | "cpm_fondo"
  | "inc_of_calibration_source_volume_at_0"
  | "counting_time"
  | "fanding"
  | "fondo_usv"
  | "dosis_transito_usv_dia"
  | "error_dosis_transito_usv_dia"
  | "calibration_source_cpm"
  | "cpm_of_the_fc_with_largest_amount_of_scintillator_AT"
  | "mass_with_greatest_amount_of_scintillator_AT"
  | "efficiency_sr_89"
  | "efficiency_sr_90"
  | "efficiency_y_90"
  | "error_cpm_contaje_1"
  | "error_cpm_contaje_2"
  | "sr89_cdes"
  | "sr90_cdes"
  | "y90_cdes"
  | "squared_counting_uncertainty_of_calibration_source_at_0"
  | "secondary_pattern_activity_of_the_fc_with_the_greatest_amount_AT"
  | "cpm_fondo_beta"
  | "calibration_source_count_rate_uncertainty_squared"
  | "spill_over"
  | "inc_squared_spill_over"
  | "dpm_fc_k40_with_less_kci"
  | "inc_squared_dpm_fc_k40_with_less_kci"
  | "k40_relative_uncertainty"
  | "uncertainty_squared_relative_inverse_sample_efficiency"
  | "inc_rel_squared_sample_vial_quantity"
  | "br_critical_limit"
  | "inc_rel_squared_performance";

export interface ProccesingTypes {
  analysis_type: number;
  analyst: number;
  preparation_procedure: number;
  start_date: string;
  amount_sample: string;
  equipment: number[];
  scintillator_lot_number: number;
  unit_types: number;
  valid_preparation: string;
  id: number;
  processing: number;
  totalSampleWaterVolume?: number;
  sampleCountVapor?: number;
  sampledAirVolume?: number;
  dataVolumeRegistries?: number[];
  numberOfTraps?: number;
  sampledAirVolumeSilicagel?: number;
  dataTrampsTritioAirArrayBefore?: number[];
  dataTrampsTritioAirArrayAfter?: number[];
  analyzedAirVolume?: number;
  analyzedWaterVolume?: number;
}

export interface ResultsTypes {
  id?: number;
  result?: boolean;
  date_end_sampling?: string;
  to_reports?: boolean;
  efficiency?: string;
  inc_rel_squared_performance?: string;
  efficiency_sr_89?: string;
  activity?: string;
  uncertainty?: string;
  lid?: string;
}

export interface AnalysisCurrent {
  id?: number;
  index?: number;
}

export interface SampligCurrent {
  id?: number;
  index?: number;
}

export type SampleStateType = {
  current: any | CurrentSample | undefined;
  id: number | undefined;
  isSave: boolean;
  ids: number[] | undefined;
};

export type SampleStateProcessingType = {
  current: any | CurrentSample | undefined;
  id: number | undefined;
  isSave: boolean;
  ids: number[] | undefined;
};

export interface CurrentSample {
  id: number;
  analyst: Analyst;
  date_start_sampling: string;
  date_end_sampling: string;
  year: number;
  week: number;
  amount_sample: string;
  unit_types: UnitTypes;
  s2_kg: string;
  ll2_m: string;
  m3_m2: string;
  proc_take_sample: string;
  masl_sampler: MaslSampler;
  type_sampler: string;
}

export interface Analyst {
  id: number;
  email: string;
}

export interface UnitTypes {
  id: number;
  codigo: string;
  descripcion: string;
}

export interface MaslSampler {
  id: number;
  email: string;
}

export enum KeysTabsAnalysis {
  H3_A = "H3-A",
  BR = "BR",
  BT = "BT",
  AT = "AT",
  H3_V = "H3-V",
  I131 = "I131",
  Sr89 = "Sr89",
  Sr90 = "Sr90",
  EG_GT = "EG-GT",
  EG_IT = "EG-IT",
  Ni63 = "Ni63",
  Fe55 = "Fe55",
  Pu238 = "Pu238",
  Pu239_240 = "Pu239+240",
  AM241 = "Am241",
  Cm243_244 = "Cm243+244",
  DP = "DP",
  Sr89_90 = "Sr89/90",
  BPP = "BPP",
  IG = "IG",
  GT = "GT",
  BTBR = "BTBR",
  AT_E = "AT-E",
  AT_C = "AT-C",
  AT_PP = "AT-P",
}

export enum TypesPreparationAT {
  E = "Evaporación",
  C = "Coprecipitación",
  PP = "Partículas de polvo/No aplica",
}

export interface MapeoTab {
  tab: SetAnalysisPhase[];
  navigator: React.ReactNode;
  form: FC<any>;
}

export interface SetAnalysisPhase {
  label: string;
  set: string;
  endpoint: string;
  component: React.ComponentType<any>;
  functionMap: (data: any) => any;
}

export interface MeasurementTypes {
  id: number;
  countings: Counting[];
}

export interface TableProcessingMapeoTypes {
  drying: DataProcessing[];
  calcinacion: DataProcessing[];
}

export interface DataProcessing {
  weight_with_capsule: string;
  type: number;
  capsule_weight?: string;
}
